import PageBreadcrumbNavigation from "@saleor/components/PageBreadcrumbNavigation";
import SaveButtonBar from "@saleor/components/SaveButtonBar";
import UnlockCodeCard from "@saleor/coupons/components/CouponUnlockCodes/subcomponents/UnlockCodeCard/UnlockCodeCard";
import {
  IUnlockCode,
  IUnlockCodeErrorDetail
} from "@saleor/coupons/components/CouponUnlockCodes/useUnlockCodesState";
import {
  generateUnlockCodeErrorDetail,
  hasError
} from "@saleor/coupons/components/CouponUnlockCodes/utils";
import DiscardWarnDialog from "@saleor/coupons/components/DiscardWarnDialog/DiscardWarnDialog";
import UnlockCodesHeader from "@saleor/coupons/components/UnlockCodesHeader/UnlockCodesHeader";
import { couponUnlockCodeEdit } from "@saleor/coupons/mutations";
import {
  couponUnlockCodeQuery,
  couponUnlockCodesStringQuery
} from "@saleor/coupons/queries";
import { CouponUnlockCodeUpdateVariables } from "@saleor/coupons/types/CouponUnlockCodeUpdate";
import { couponUrl } from "@saleor/coupons/url";
import useAppState from "@saleor/hooks/useAppState";
import useNotifier from "@saleor/hooks/useNotifier";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "react-apollo";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";

import useStyles from "./styles";

interface ICouponEditUnlockCodePageProps {
  couponId: string;
  unlockCodeId: string;
}

const CouponEditUnlockCodePage: FC<ICouponEditUnlockCodePageProps> = ({
  couponId,
  unlockCodeId
}) => {
  const intl = useIntl();
  const notify = useNotifier();
  const classes = useStyles({});
  const navigate = useNavigate();
  const [editUnlockCodeState, setEditUnlockCodeState] = useState<
    IUnlockCode | undefined
  >(undefined);
  const [, dispatchAppState] = useAppState();
  const [errorDetails, setErrorDetails] = useState<IUnlockCodeErrorDetail>({});
  const [isDiscardWarnShown, setIsDiscardWarnShown] = useState(false);

  const {
    loading: isCouponUnlockCodeQueryLoading,
    data: unlockCodeData
  } = useQuery(couponUnlockCodeQuery, {
    fetchPolicy: "network-only",
    variables: {
      unlockCodeId
    }
  });

  const {
    loading: isCouponUnlockCodesQueryLoading,
    data: existingUnlockCodesData
  } = useQuery(couponUnlockCodesStringQuery, {
    fetchPolicy: "network-only",
    variables: { couponId }
  });

  useEffect(() => {
    if (unlockCodeData !== undefined) {
      setEditUnlockCodeState({
        componentKey: 0,
        unlockCode: unlockCodeData.couponUnlockCode.code,
        couponReportTags: unlockCodeData.couponUnlockCode.tags,
        description: unlockCodeData.couponUnlockCode.description,
        unlockCodeUsageLimit: unlockCodeData.couponUnlockCode.usageLimit
      });
    }
  }, [unlockCodeData]);

  const [
    unlockCodeEditMutation,
    { loading: isBulkEditMutationLoading }
  ] = useMutation<CouponUnlockCodeUpdateVariables>(couponUnlockCodeEdit);

  const breadcrumbDirectory = useMemo(
    () => [
      {
        name:
          unlockCodeData?.couponUnlockCode.coupon.mainPromoTitle ??
          "Create coupon",
        url: couponUrl(couponId)
      }
    ],
    [unlockCodeData, couponId]
  );

  const handleBackNavigate = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleEditUnlockCode = useCallback(
    (modifiedUnlockCodeData: IUnlockCode) => {
      setEditUnlockCodeState(modifiedUnlockCodeData);
    },
    []
  );

  useEffect(() => {
    dispatchAppState({
      payload: {
        value: isCouponUnlockCodeQueryLoading
      },
      type: "displayLoader"
    });
  }, [isCouponUnlockCodeQueryLoading]);

  const handleSave = useCallback(() => {
    const otherExistingUnlockCodes: string[] = existingUnlockCodesData.couponUnlockCodes.edges
      .map(existingCode => existingCode.node.code)
      .filter(
        existingCode => existingCode !== unlockCodeData.couponUnlockCode.code
      );
    const errorDetail = generateUnlockCodeErrorDetail(
      editUnlockCodeState,
      [],
      otherExistingUnlockCodes
    );
    if (!hasError(errorDetail)) {
      const payload: CouponUnlockCodeUpdateVariables = {
        id: unlockCodeId,
        input: {
          code: editUnlockCodeState.unlockCode,
          usageLimit: editUnlockCodeState.unlockCodeUsageLimit,
          tags: editUnlockCodeState.couponReportTags,
          description: editUnlockCodeState.description
        }
      };
      dispatchAppState({
        payload: {
          value: true
        },
        type: "displayLoader"
      });
      unlockCodeEditMutation({ variables: payload })
        .then(() => {
          notify({
            status: "success",
            text: intl.formatMessage({
              defaultMessage: "Unlock code edited successfully."
            })
          });
          navigate(couponUrl(couponId));
        })
        .catch(() => {
          notify({
            status: "error",
            text: intl.formatMessage({
              defaultMessage: "An error occured."
            })
          });
        })
        .finally(() =>
          dispatchAppState({
            payload: {
              value: false
            },
            type: "displayLoader"
          })
        );
    } else {
      setErrorDetails(errorDetail);
    }
  }, [
    unlockCodeId,
    editUnlockCodeState,
    couponId,
    existingUnlockCodesData,
    unlockCodeData
  ]);

  const onToggleShownWarnDialog = useCallback(
    (isShown: boolean) => () => {
      setIsDiscardWarnShown(isShown);
    },
    []
  );

  if (
    editUnlockCodeState === undefined ||
    isCouponUnlockCodeQueryLoading ||
    isCouponUnlockCodesQueryLoading
  ) {
    return <div />;
  }

  return (
    <>
      <div className={classes.container}>
        <PageBreadcrumbNavigation items={breadcrumbDirectory} />
        <UnlockCodesHeader
          couponCode={unlockCodeData.couponUnlockCode.coupon.code}
          onBackClick={onToggleShownWarnDialog(true)}
        />
        <UnlockCodeCard
          onModifyUnlockCodeData={handleEditUnlockCode}
          unlockCodeErrors={errorDetails}
          unlockCodeData={editUnlockCodeState}
          isDisabled={false}
        />
        <SaveButtonBar
          state="default"
          onCancel={onToggleShownWarnDialog(true)}
          onSave={handleSave}
          disabled={isBulkEditMutationLoading}
          labels={{ cancel: "Discard" }}
          disabledCancelBtn={isBulkEditMutationLoading}
        />
      </div>
      <DiscardWarnDialog
        isDialogShown={isDiscardWarnShown}
        onRedirect={handleBackNavigate}
        onClose={onToggleShownWarnDialog(false)}
      />
    </>
  );
};

export default CouponEditUnlockCodePage;
